import { supabase } from '../../../shared/lib/supabase';

// Define the SeasonAccess interface
export interface SeasonAccess {
  id: string;
  season_id: string;
  organisation_id: string;
  access_type: string;
  context: string | null;
  granted_by: string | null;
  granted_at: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  season_name?: string;
  organisation_name?: string;
}

export interface SeasonAccessCreateInput {
  season_id: string;
  organisation_id: string;
  access_type: string;
  context?: string | null;
  granted_by?: string | null;
  granted_at: string;
  active: boolean;
}

export interface SeasonAccessUpdateInput {
  season_id?: string;
  access_type?: string;
  context?: string | null;
  granted_by?: string | null;
  granted_at?: string;
  active?: boolean;
}

class SeasonAccessService {
  async getAll(): Promise<SeasonAccess[]> {
    try {
      console.log('🔍 Fetching season access with joins...');

      const { data, error } = await supabase
        .from('season_access')
        .select(`
          *,
          seasons!season_access_season_id_fkey(name),
          organisations!season_access_organisation_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching season access:', error);
        throw error;
      }

      const transformed = (data || []).map((item: any) => ({
        ...item,
        season_name: item.seasons?.name || item.season_name,
        organisation_name: item.organisations?.name || item.organisation_name
      }));

      console.log('✅ Season access fetched successfully:', transformed.length || 0, 'records');
      return transformed;
    } catch (error) {
      console.error('❌ Error in getAll:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<SeasonAccess | null> {
    try {
      console.log('🔍 Fetching season access by ID:', id);

      const { data, error } = await supabase
        .from('season_access')
        .select(`
          *,
          seasons!season_access_season_id_fkey(name),
          organisations!season_access_organisation_id_fkey(name)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('❌ Error fetching season access by ID:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, season_name: (data as any).seasons?.name, organisation_name: (data as any).organisations?.name }
        : null;

      console.log('✅ Season access fetched successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in getById:', error);
      throw error;
    }
  }

  async create(input: SeasonAccessCreateInput): Promise<SeasonAccess> {
    try {
      console.log('🔍 Creating season access:', input);

      const { data, error } = await supabase
        .from('season_access')
        .insert([input])
        .select(`
          *,
          seasons!season_access_season_id_fkey(name),
          organisations!season_access_organisation_id_fkey(name)
        `)
        .single();

      if (error) {
        console.error('❌ Error creating season access:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, season_name: (data as any).seasons?.name, organisation_name: (data as any).organisations?.name }
        : (data as any);

      console.log('✅ Season access created successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in create:', error);
      throw error;
    }
  }

  async update(id: string, input: SeasonAccessUpdateInput): Promise<SeasonAccess> {
    try {
      console.log('🔍 Updating season access:', id, input);

      const { data, error } = await supabase
        .from('season_access')
        .update(input)
        .eq('id', id)
        .select(`
          *,
          seasons!season_access_season_id_fkey(name),
          organisations!season_access_organisation_id_fkey(name)
        `)
        .single();

      if (error) {
        console.error('❌ Error updating season access:', error);
        throw error;
      }

      const transformed = data
        ? { ...data, season_name: (data as any).seasons?.name, organisation_name: (data as any).organisations?.name }
        : (data as any);

      console.log('✅ Season access updated successfully');
      return transformed as any;
    } catch (error) {
      console.error('❌ Error in update:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log('🔍 Deleting season access:', id);

      const { error } = await supabase
        .from('season_access')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting season access:', error);
        throw error;
      }

      console.log('✅ Season access deleted successfully');
    } catch (error) {
      console.error('❌ Error in delete:', error);
      throw error;
    }
  }
}

export const seasonAccessService = new SeasonAccessService();
